import { useEffect, useState } from "react";

/**
 * Debug panel interno — visible únicamente cuando la URL contiene
 * `?debug=<LEAD_STATS_TOKEN>`. Consume /api/leads/stats. Muestra:
 * totales, canales configurados, top sources, últimos leads (masked).
 */
export default function DebugPanel({ token }) {
  const [data, setData] = useState(null);
  const [abData, setAbData] = useState(null);
  const [err, setErr] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let stopped = false;
    const fetchIt = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const [statsRes, abRes] = await Promise.all([
          fetch(`${base}/api/leads/stats?token=${encodeURIComponent(token)}`),
          fetch(`${base}/api/ab/experiments?token=${encodeURIComponent(token)}`),
        ]);
        if (!statsRes.ok) throw new Error(`HTTP ${statsRes.status}`);
        const j = await statsRes.json();
        const ab = abRes.ok ? await abRes.json() : { experiments: [] };
        if (!stopped) {
          setData(j);
          setAbData(ab);
          setErr(null);
        }
      } catch (e) {
        if (!stopped) setErr(String(e.message || e));
      }
    };
    fetchIt();
    const interval = setInterval(() => {
      setNow(Date.now());
      fetchIt();
    }, 8000);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [token]);

  if (err) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <p className="font-body text-sm text-[#c33]">Debug panel error: {err}</p>
        <p className="mt-2 font-body text-xs text-[#999]">
          Verifica que el token en la URL sea el mismo que <code>LEAD_STATS_TOKEN</code>.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <p className="font-body text-sm text-[#666]">Cargando métricas...</p>
      </div>
    );
  }

  const T = data.totals || {};
  const CH = data.channels || {};

  return (
    <div
      data-testid="debug-panel"
      className="min-h-[100dvh] bg-[#0b0b0c] px-5 py-10 text-[#e8e8ea] lg:px-10"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-wrap items-baseline justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <h1 className="font-serif-display text-2xl">KELO DIAMONDS — Live Diagnostics</h1>
            <p className="mt-1 font-body text-[11px] uppercase tracking-[0.2em] text-white/40">
              Generated {new Date(data.generated_at).toLocaleString()}
            </p>
          </div>
          <div className="flex gap-2">
            <ChannelChip label="Email (Resend)" ok={CH.email_configured} />
            <ChannelChip label="SMS (Twilio)" ok={CH.sms_configured} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="All time" value={T.all_time} />
          <Stat label="Today" value={T.today} highlight />
          <Stat label="Last 7d" value={T.last_7d} />
          <Stat label="Step 2 done" value={T.step2_completed} />
          <Stat label="Email delivered" value={T.email_delivered} />
          <Stat label="SMS delivered" value={T.sms_delivered} />
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_2fr]">
          <div>
            <h2 className="mb-3 font-body text-xs uppercase tracking-[0.24em] text-white/50">
              Top sources
            </h2>
            <ul className="divide-y divide-white/10 rounded-lg border border-white/10 bg-white/[0.02]">
              {(data.by_source || []).map((s) => (
                <li
                  key={s.source}
                  className="flex items-center justify-between px-4 py-3 font-mono text-[13px]"
                >
                  <span className="text-white/80">{s.source}</span>
                  <span className="tabular-nums text-white/60">{s.n}</span>
                </li>
              ))}
              {(!data.by_source || data.by_source.length === 0) && (
                <li className="px-4 py-3 font-mono text-[13px] text-white/40">— empty —</li>
              )}
            </ul>
          </div>

          <div>
            <h2 className="mb-3 font-body text-xs uppercase tracking-[0.24em] text-white/50">
              Recent leads (masked)
            </h2>
            <div className="overflow-hidden rounded-lg border border-white/10 bg-white/[0.02]">
              <table className="min-w-full font-mono text-[12px]">
                <thead className="bg-white/[0.04] text-white/50">
                  <tr>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Phone</th>
                    <th className="px-3 py-2 text-left">Source</th>
                    <th className="px-3 py-2 text-left">Step</th>
                    <th className="px-3 py-2 text-left">Delivered</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent || []).map((r, i) => (
                    <tr key={i} className="border-t border-white/5 text-white/80">
                      <td className="whitespace-nowrap px-3 py-2 text-white/50">
                        {formatShort(r.created_at)}
                      </td>
                      <td className="px-3 py-2">{r.first_name || "—"}</td>
                      <td className="px-3 py-2">{r.email || "—"}</td>
                      <td className="px-3 py-2">{r.phone || "—"}</td>
                      <td className="px-3 py-2 text-white/60">{r.source}</td>
                      <td className="px-3 py-2 text-center">{r.step}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`mr-1 inline-block h-2 w-2 rounded-full ${
                            r.email_delivered ? "bg-emerald-400" : "bg-white/15"
                          }`}
                          title="email"
                        />
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${
                            r.sms_delivered ? "bg-emerald-400" : "bg-white/15"
                          }`}
                          title="sms"
                        />
                      </td>
                    </tr>
                  ))}
                  {(!data.recent || data.recent.length === 0) && (
                    <tr>
                      <td className="px-3 py-4 text-white/40" colSpan={7}>
                        — no leads yet —
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-4 font-mono text-[11px] text-white/30">
          Auto-refresh every 8s · {new Date(now).toLocaleTimeString()}
        </div>

        {/* A/B experiments — sección extra */}
        <div className="mt-10">
          <h2 className="mb-4 font-body text-xs uppercase tracking-[0.24em] text-white/50">
            A/B experiments
          </h2>
          {(!abData || !abData.experiments || abData.experiments.length === 0) ? (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-4 font-mono text-[12px] text-white/40">
              — no experiments yet. Impressions and conversions will appear here as visitors interact.
            </div>
          ) : (
            <div className="space-y-6">
              {abData.experiments.map((exp) => (
                <div
                  key={exp.experiment_id}
                  data-testid={`ab-exp-${exp.experiment_id}`}
                  className="rounded-lg border border-white/10 bg-white/[0.02] p-4"
                >
                  <div className="mb-3 flex items-baseline justify-between">
                    <span className="font-mono text-[13px] text-white/85">{exp.experiment_id}</span>
                    {exp.goal && (
                      <span className="font-mono text-[11px] text-white/40">goal: {exp.goal}</span>
                    )}
                  </div>
                  <table className="min-w-full font-mono text-[12px]">
                    <thead className="text-white/50">
                      <tr>
                        <th className="px-2 py-1 text-left">Variant</th>
                        <th className="px-2 py-1 text-right">Impressions</th>
                        <th className="px-2 py-1 text-right">Conversions</th>
                        <th className="px-2 py-1 text-right">CTR %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(exp.stats || []).map((s) => (
                        <tr key={s.name} className="border-t border-white/5 text-white/80">
                          <td className="px-2 py-1.5">{s.name}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{s.impressions}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{s.conversions}</td>
                          <td
                            className={`px-2 py-1.5 text-right tabular-nums ${
                              s.ctr_pct > 0 ? "text-emerald-300" : "text-white/40"
                            }`}
                          >
                            {s.ctr_pct}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div
      className={`rounded-lg border border-white/10 bg-white/[0.02] px-4 py-4 ${
        highlight ? "ring-1 ring-white/20" : ""
      }`}
    >
      <div className="font-body text-[10px] uppercase tracking-[0.24em] text-white/45">{label}</div>
      <div className="mt-1 font-serif-display text-3xl tabular-nums text-white">
        {value ?? 0}
      </div>
    </div>
  );
}

function ChannelChip({ label, ok }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[11px] ${
        ok ? "border-emerald-400/40 text-emerald-300" : "border-white/15 text-white/50"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-white/30"}`}
      />
      {label} · {ok ? "on" : "off"}
    </span>
  );
}

function formatShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16);
  }
}
